import { useEffect, useRef, useState } from "react";
import { convertFromWei } from "../utils/convertNumber";
import getTokenInfo from "../utils/checkInfoByAccount";
import ABI_TOKEN from "../constant/ABI/abiKIB.json";
import ABI_STAKING from "../constant/ABI/abiStaking.json";
import { getPoolApr } from "../utils/apr";
import axios from "axios";
import Web3 from "web3";
import { useSelector } from "react-redux";

const web3 = new Web3("https://avalanche.drpc.org");

export const useUserStakingInfo = (address: string, dataPools: any[]) => {
  const [userProfile, setUserProfile] = useState<{ [key: number]: any }>({});
  const [tokenPrices, setTokenPrices] = useState<{ [key: number]: any }>({});
  const tokenPricesRef = useRef<{ [key: number]: any }>({});
  const { kibPrice }: any = useSelector((state: any) => state.pool);

  const getDecimalToken = async (token: string) => {
    try {
      const contract = new web3.eth.Contract(ABI_TOKEN, token);
      return await contract.methods.decimals().call();
    } catch {
      return 18;
    }
  };

  const fetchPrices = async () => {
    try {
      const tokenAddresses = dataPools
        .flatMap((pool) => [pool.tokenStake, pool.tokenReward])
        .filter(Boolean)
        .filter((val, i, arr) => arr.indexOf(val) === i)
        .join(",");

      const res = await axios.get(
        `https://api.geckoterminal.com/api/v2/simple/networks/avax/token_price/${tokenAddresses}`
      );

      const prices = res.data?.data?.attributes?.token_prices || {};
      const grouped = Object.fromEntries(
        dataPools.map((pool) => [
          pool.id,
          {
            stake: Number(kibPrice) || "0",
            reward: prices[pool.tokenReward.toLowerCase()] || "0.001",
          },
        ])
      );

      setTokenPrices(grouped);
      tokenPricesRef.current = grouped;
    } catch (err) {
      console.error("Error fetching token prices:", err);
    }
  };

  const fetchUserProfile = async () => {
    try {
      const promises = dataPools.map(async (pool) => {
        try {
          const staking = new web3.eth.Contract(ABI_STAKING, pool.contract);
          const contractStake = new web3.eth.Contract(
            ABI_TOKEN,
            pool.tokenStake
          );

          const promisesList = [
            address
              ? staking.methods.userInfo(address).call()
              : Promise.resolve({}),
            address
              ? staking.methods.pendingReward(address).call()
              : Promise.resolve({}),
            staking.methods.totalStaked().call(),
            contractStake.methods.balanceOf(pool.contract).call(),
          ];

          const [info, pending, totalTokenStaked, balance] = await Promise.all(
            promisesList
          );

          const tokenInfo = address
            ? await getTokenInfo(pool.contract, pool.tokenStake, address)
            : null;

          const tokenRewardDecimal = await getDecimalToken(pool.tokenReward);
          const totalStaked = Number(convertFromWei(totalTokenStaked, 18));
          const apr = getPoolApr(
            Number(tokenPricesRef.current[pool.id]?.stake || 0),
            Number(tokenPricesRef.current[pool.id]?.reward || 0),
            totalStaked,
            Number(pool.tokenPerBlock),
            Number(tokenRewardDecimal)
          );

          return {
            id: pool.id,
            data: {
              info,
              pendingReward: pending || null,
              balanceOf: balance,
              detailsToken: tokenInfo || null,
              apr,
            },
          };
        } catch (err) {
          console.warn(`Error fetching pool ${pool.id}:`, err);
        }
      });

      const results = await Promise.all(promises);
      const profile = results.reduce((acc: any, item) => {
        if (item && item.data) acc[item.id] = item.data;
        return acc;
      }, {});

      setUserProfile((prevProfile) => ({
        ...prevProfile,
        ...profile,
      }));
    } catch (err) {
      console.error("Error fetching user profile:", err);
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      await fetchPrices();
      await fetchUserProfile();
    };

    const priceInterval = setInterval(() => {
      fetchPrices();
    }, 5000);

    fetchData();

    const interval = setInterval(() => {
      fetchUserProfile();
    }, 10000);

    return () => {
      clearInterval(priceInterval);
      clearInterval(interval);
    };
  }, [address, kibPrice, tokenPricesRef]);

  return { userProfile, tokenPrices, fetchUserProfile, fetchPrices };
};
